import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Calendar, Clock, Video, MapPin, Users, Plus, List, Grid3X3 } from 'lucide-react'
import { interviewsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { InterviewCalendar } from '@/components/features/interviews/InterviewCalendar'
import { PageLayout } from '@/components/layout/PageLayout'
import { formatDate, getInitials } from '@/lib/utils'

const statusColors: Record<string, string> = {
  scheduled: 'bg-primary/10 text-primary',
  completed: 'bg-success/10 text-success',
  cancelled: 'bg-muted text-muted-foreground',
  no_show: 'bg-destructive/10 text-destructive',
}

const typeIcons: Record<string, typeof Video> = {
  video: Video,
  phone: Clock,
  onsite: MapPin,
}

type ViewMode = 'list' | 'calendar'

export function InterviewsPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('calendar')

  const { data, isLoading } = useQuery({
    queryKey: ['interviews'],
    queryFn: () => interviewsApi.list(),
  })

  const interviews = (data as any[]) || []

  // Group interviews by date for list view
  const groupedInterviews = interviews.reduce((groups: Record<string, any[]>, interview) => {
    const date = formatDate(interview.scheduledAt)
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(interview)
    return groups
  }, {})

  const toolbar = (
    <div className="flex items-center justify-end gap-2">
      <div className="flex border rounded-lg overflow-hidden">
        <button
          onClick={() => setViewMode('calendar')}
          className={`
            p-1.5 transition-colors
            ${viewMode === 'calendar' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
          `}
          title="Calendar view"
        >
          <Grid3X3 className="h-4 w-4" />
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`
            p-1.5 transition-colors
            ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
          `}
          title="List view"
        >
          <List className="h-4 w-4" />
        </button>
      </div>
      <Button size="sm" asChild>
        <Link to="/interviews/new">
          <Plus className="h-4 w-4 mr-1" />
          Schedule
        </Link>
      </Button>
    </div>
  )

  return (
    <PageLayout fullHeight toolbar={toolbar}>
      {/* Content */}
      <div className="h-full flex flex-col">
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-lg" />
          ))}
        </div>
      ) : viewMode === 'calendar' ? (
        <InterviewCalendar
          interviews={interviews.map((i: any) => ({
            id: i.id,
            title: i.title,
            type: i.type,
            scheduledAt: new Date(typeof i.scheduledAt === 'number' ? i.scheduledAt * 1000 : i.scheduledAt).toISOString(),
            duration: i.durationMinutes || i.duration || 60,
            status: i.status,
            application: {
              id: i.application?.id,
              candidate: {
                id: i.application?.candidate?.id,
                name: `${i.application?.candidate?.firstName || ''} ${i.application?.candidate?.lastName || ''}`.trim() || 'Unknown',
                avatarUrl: i.application?.candidate?.avatarUrl,
              },
              job: {
                id: i.application?.job?.id,
                title: i.application?.job?.title || 'Unknown Position',
              },
            },
            interviewers: i.participants?.map((p: any) => ({
              id: p.user?.id || p.id,
              name: p.user?.name || 'Unknown',
              avatarUrl: p.user?.avatarUrl,
            })) || [],
          }))}
        />
      ) : interviews.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-1">No interviews scheduled</h3>
            <p className="text-muted-foreground mb-4">
              Schedule interviews with candidates from their application page
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6 flex-1 min-h-0 overflow-y-auto">
          {Object.entries(groupedInterviews).map(([date, dateInterviews]) => (
            <div key={date}>
              <h2 className="text-sm font-semibold mb-3">{date}</h2>
              <div className="space-y-3">
                {dateInterviews.map((interview: any, index: number) => {
                  const TypeIcon = typeIcons[interview.type] || Video

                  return (
                    <motion.div
                      key={interview.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15, delay: index * 0.03 }}
                    >
                      <Card className="hover:shadow-md transition-all cursor-pointer">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Time */}
                            <div className="text-center min-w-[80px]">
                              <div className="text-2xl font-bold">
                                {new Date(typeof interview.scheduledAt === 'number' ? interview.scheduledAt * 1000 : interview.scheduledAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true,
                                })}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {interview.durationMinutes || interview.duration || 60} min
                              </div>
                            </div>

                            {/* Divider */}
                            <div className="w-px h-16 bg-border" />

                            {/* Details */}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{interview.title}</h3>
                                <Badge className={statusColors[interview.status]}>
                                  {interview.status.replace('_', ' ')}
                                </Badge>
                              </div>

                              {/* Candidate */}
                              {interview.application?.candidate && (
                                <div className="flex items-center gap-2 text-sm mb-2">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs">
                                      {getInitials(
                                        `${interview.application.candidate.firstName} ${interview.application.candidate.lastName}`
                                      )}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span>
                                    {interview.application.candidate.firstName}{' '}
                                    {interview.application.candidate.lastName}
                                  </span>
                                  <span className="text-muted-foreground">
                                    for {interview.application.job?.title}
                                  </span>
                                </div>
                              )}

                              {/* Meta */}
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <TypeIcon className="h-3.5 w-3.5" />
                                  <span className="capitalize">{interview.type}</span>
                                </span>
                                {interview.participants && (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3.5 w-3.5" />
                                    {interview.participants.length} interviewers
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Participants */}
                            {interview.participants && interview.participants.length > 0 && (
                              <div className="flex -space-x-2">
                                {interview.participants.slice(0, 3).map((p: any) => (
                                  <Avatar
                                    key={p.id}
                                    className="h-8 w-8 border-2 border-background"
                                  >
                                    <AvatarImage src={p.user?.avatarUrl} />
                                    <AvatarFallback className="text-xs">
                                      {p.user ? getInitials(p.user.name) : '?'}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                                {interview.participants.length > 3 && (
                                  <div className="h-8 w-8 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-medium">
                                    +{interview.participants.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </PageLayout>
  )
}
